"use client";

import { useGetVision } from "@/app/hooks/main-hooks";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import Image from "next/image";
import { FiTarget } from "react-icons/fi";

export default function Vision() {
  const { data } = useGetVision();
  const locale = useLocale();

  const vision = data?.vision || {};

  return (
    <section
      id="vision"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ backgroundColor: "#0a1b2e" }} // --color-navy-primary
    >
      {/* Subtle Gradient Overlay */}
      <div className="absolute inset-0 bg-linear-to-b from-transparent via-[#132b44]/50 to-[#0a1b2e]" />

      {/* Floating Gold Blobs */}
      <motion.div
        animate={{ y: [0, -30, 0], rotate: [0, 15, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-10 left-10 w-96 h-96 bg-[#c9a24d]/10 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ y: [0, 30, 0], rotate: [0, -15, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-10 right-10 w-80 h-80 bg-[#e6c77a]/10 rounded-full blur-3xl"
      />

      {/* Background Illustration (Optional - Highly Recommended) */}
      <div className="absolute inset-0 opacity-20">
        <Image
          src="/vision-bg.jpg"
          alt=""
          fill
          className="object-cover"
          priority
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center"
        >
          {/* Title */}
          <h2 className="text-5xl lg:text-6xl font-extrabold text-white mb-6">
            {locale === "en" && "Our"}{" "}
            <span className="bg-linear-to-r from-[#c9a24d] to-brand-mustard bg-clip-text text-transparent">
              {locale === "en" ? "Vision" : "رؤيتنا"}
            </span>
          </h2>

          {/* Target Icon with Pulse */}
          <motion.div
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#c9a24d]/20 mb-10"
          >
            <FiTarget className="text-5xl text-[#c9a24d]" />
          </motion.div>

          {/* Vision Statement */}
          <p className="text-xl lg:text-2xl text-[#e6c77a] leading-relaxed max-w-4xl mx-auto mb-16 px-4">
            {vision?.mainMessage?.[locale]}
          </p>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {vision?.features?.map((pillar: any, index: number) => (
              <motion.div
                key={pillar?._id || pillar?.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="group relative p-8 rounded-2xl bg-[#132b44]/60 backdrop-blur-sm border border-[#c9a24d]/30 overflow-hidden"
              >
                {/* Gold Shine Effect on Hover */}
                <div className="absolute inset-0 bg-linear-to-br from-[#c9a24d]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative z-10">
                  <h3 className="text-3xl font-bold text-[#c9a24d] mb-4">
                    {pillar?.name?.[locale]}
                  </h3>
                  <p className="text-lg text-white/80">
                    {pillar?.description?.[locale]}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
